import { useEffect } from 'react';
import { generatePath, NavLink } from 'react-router-dom';
import {
  SelectSize,
  InputText,
  InputSize,
  Checkbox,
  Select,
  IconUp,
  IconDown,
  ButtonSecondary,
  ButtonSize,
  IconSelected,
  IconBack,
  PermissionEnum,
  IconChangeLoClock,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  AccessRight,
  CofApprovalStatus,
  NatureOfControlEnum,
  NewApplicationStatus,
  ReviewStatus,
  SignatureType,
} from '../../../../hoc/new-application/types';
import InfoRow from '../../common/info-row';
import getStatusClassName from '../../../../hoc/new-application/helpers/get-status-class-name';
import { ApplicationCOFTypes } from './types';
import useCompanyOfficial from '../hooks/use-company-official';

import ROUTES from '../../../../routes';

import cn from '../styles.module.css';

const CompanyOfficial = ({
  value,
  setValue,
  setValuesList,
  index,
  values,
  applicationId,
  companyOfficial,
  disabled,
  type,
  applicationStatus,
  initialFields,
  fields,
  onApplicationSubmit,
  approveDisabled,
  questionnarieFields,
  defaultQuestionnarieFormValues,
  applicationScheme,
  auditLogAvailable,
  getApplicationChat,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
}: ApplicationCOFTypes) => {
  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    updateStatusInProgress,
    cofUpdateInProgress,
    onUpdateStatus,
    setHavingControl,
    setCompanyOfficialPosition,
    setCompanyOfficialNatureOfControl,
    setCompanyOfficialPercentage,
    setCompanyOfficialSignatureType,
    setCompanyOfficialAccessRight,
    setDefaultValues,
    isError,
    defaultValues,
    companyOfficialPercentage,
    setError,
    focused,
    setCollapsed,
    collapsed,
    setFocused,
    updateInProgress,
    companyOfficialPosition,
    onChange,
    companyOfficialSignatureType,
    companyOfficialAccessRight,
    havingControl,
    error,
    companyOfficialNatureOfControl,
    onUpdate,
  } = useCompanyOfficial({
    values,
    index,
    setValue,
    setValuesList,
    value,
    companyOfficial,
    fields,
    applicationId,
    initialFields,
    onApplicationSubmit,
    questionnarieFields,
    defaultQuestionnarieFormValues,
    applicationScheme,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    getApplicationChat,
    type,
  });

  const { statusClassName } = getStatusClassName({
    status: companyOfficial?.subApplication.status as NewApplicationStatus,
  });

  let cofApprovalStatus = 'Awaiting Approval';
  let cofApprovalStatusClassName = cn.incompleteStatus;
  let cofApplicationDescription = 'Application is incomplete';
  let cofApplicationStatus = 'Incomplete';
  let cofApprovalButton;

  if (
    companyOfficial?.status !== CofApprovalStatus.ACCEPTED &&
    companyOfficial?.status !== CofApprovalStatus.DECLINED &&
    companyOfficial?.subApplication.status === NewApplicationStatus.ACCEPTED
  ) {
    let buttonText;

    if (updateStatusInProgress || cofUpdateInProgress) {
      buttonText = 'Loading...';
    } else if (!updateStatusInProgress && type === 'MR') {
      buttonText = 'Approve as Main Representative';
    } else if (!updateStatusInProgress && type === 'COF') {
      buttonText = 'Approve as Company Official';
    }

    cofApprovalButton = (
      <ButtonSecondary
        className={`${cn.cofApproveButton} ${
          !value.position ||
          !value.signatureType ||
          (type === 'MR' && approveDisabled)
            ? cn.cofApproveButtonDisabled
            : ''
        }`}
        size={ButtonSize.SMALL}
        icon={!updateStatusInProgress ? <IconSelected /> : undefined}
        text={buttonText}
        disabled={
          !value.position ||
          !value.signatureType ||
          (type === 'MR' && approveDisabled)
        }
        onClick={() => onUpdateStatus(CofApprovalStatus.ACCEPTED)}
        tabIndex={-1}
      />
    );
  } else if (companyOfficial?.status === CofApprovalStatus.ACCEPTED) {
    cofApprovalButton = (
      <ButtonSecondary
        className={cn.cofApproveButton}
        size={ButtonSize.SMALL}
        icon={
          !updateStatusInProgress ? (
            <IconBack className={cn.backIcon} />
          ) : undefined
        }
        text={
          updateStatusInProgress || cofUpdateInProgress
            ? 'Loading...'
            : 'Cancel approval'
        }
        tabIndex={-1}
        onClick={() => onUpdateStatus(CofApprovalStatus.INPROCESS)}
      />
    );
  } else if (companyOfficial?.status === CofApprovalStatus.DECLINED) {
    cofApprovalButton = (
      <ButtonSecondary
        className={cn.cofApproveButton}
        size={ButtonSize.SMALL}
        icon={
          !updateStatusInProgress ? (
            <IconBack className={cn.backIcon} />
          ) : undefined
        }
        tabIndex={-1}
        text={
          updateStatusInProgress || cofUpdateInProgress
            ? 'Loading...'
            : 'Cancel Decline'
        }
        onClick={() => onUpdateStatus(CofApprovalStatus.INPROCESS)}
      />
    );
  }

  if (companyOfficial?.status === CofApprovalStatus.ACCEPTED) {
    cofApprovalStatus = 'Accepted';
    cofApprovalStatusClassName = cn.acceptedStatus;
  } else if (companyOfficial?.status === CofApprovalStatus.DECLINED) {
    cofApprovalStatus = 'Declined';
    cofApprovalStatusClassName = cn.declinedStatus;
  } else if (
    companyOfficial?.subApplication.status === NewApplicationStatus.DECLINED
  ) {
    cofApprovalStatusClassName = cn.declinedStatus;

    if (type === 'MR') {
      cofApprovalStatus = 'Customer can`t be approval as Main Representative';
    } else {
      cofApprovalStatus = 'Customer can`t be approval as Company Official';
    }
  }

  if (
    companyOfficial?.subApplication.status === NewApplicationStatus.DECLINED
  ) {
    cofApplicationDescription = 'Application declined';
  }

  if (
    companyOfficial?.subApplication.reviewStatus ===
      ReviewStatus.HAS_DUPLICATE &&
    companyOfficial?.subApplication.status !== NewApplicationStatus.DECLINED
  ) {
    cofApplicationDescription = 'Application in warning';
  }

  if (
    companyOfficial?.subApplication.reviewStatus ===
      ReviewStatus.HAS_DUPLICATE &&
    companyOfficial.subApplication.status !== NewApplicationStatus.DECLINED
  ) {
    cofApplicationStatus = 'Warning';
  } else if (companyOfficial && companyOfficial.subApplication.status) {
    cofApplicationStatus = `${companyOfficial.subApplication.status.charAt(
      0,
    )}${companyOfficial.subApplication.status
      .slice(1)
      .toLowerCase()
      .split('_')
      .join(' ')}`;
  }

  let cofLink;

  if (
    companyOfficial &&
    companyOfficial?.subApplication.status !== NewApplicationStatus.ACCEPTED
  ) {
    if (companyOfficial?.subApplication.type === 'INDIVIDUAL') {
      cofLink = generatePath(ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO, {
        applicationId: companyOfficial?.subApplication.id,
      });
    } else {
      cofLink = generatePath(
        ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL,
        {
          applicationId: companyOfficial?.subApplication.id,
        },
      );
    }
  } else if (
    companyOfficial &&
    companyOfficial?.subApplication.status === NewApplicationStatus.ACCEPTED &&
    companyOfficial.subApplication.organizationId
  ) {
    cofLink = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE, {
      customerId: companyOfficial?.subApplication.organizationId,
    });
  }

  useEffect(() => {
    if (value.percentage || value.natureOfControl) {
      setHavingControl(true);
    }
  }, [value]);

  useEffect(() => {
    if (companyOfficial) {
      if (companyOfficial.position) {
        setCompanyOfficialPosition(companyOfficial.position);
      }

      if (companyOfficial?.natureOfControl) {
        setCompanyOfficialNatureOfControl(companyOfficial.natureOfControl);
      }

      if (companyOfficial?.percentage) {
        setCompanyOfficialPercentage(companyOfficial.percentage);
      }

      if (companyOfficial?.signatureType) {
        setCompanyOfficialSignatureType(companyOfficial.signatureType);
      }

      if (companyOfficial?.accessRight) {
        setCompanyOfficialAccessRight(companyOfficial.accessRight);
      }

      setDefaultValues({
        position: companyOfficial.position,
        percentage: companyOfficial.percentage || '',
        natureOfControl: companyOfficial.natureOfControl || '',
        signatureType: companyOfficial.signatureType || '',
        accessRight: companyOfficial.accessRight || AccessRight.NONE,
      });
    }
  }, [companyOfficial]);

  useEffect(() => {
    if (isError) {
      setCompanyOfficialNatureOfControl(defaultValues.natureOfControl);
      setCompanyOfficialPercentage(defaultValues.percentage);
      setCompanyOfficialPosition(defaultValues.position);
      setCompanyOfficialSignatureType(defaultValues.signatureType);
      setCompanyOfficialAccessRight(defaultValues.accessRight);
    }
  }, [isError]);

  useEffect(() => {
    if (
      companyOfficialPercentage &&
      !/^\d*\.?\d*$/.test(companyOfficialPercentage)
    ) {
      setError('Percentage of control field must be numeric');
    } else {
      setError('');
    }
  }, [companyOfficialPercentage]);

  return (
    <div className={cn.mainRepresentativeApplicationsWrapper}>
      {companyOfficial && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          onClick={() => !focused && setCollapsed(!collapsed)}
          className={`${cn.mainRepresentativeApplication} ${
            collapsed ? cn.mainRepresentativeApplicationCollapsed : ''
          }`}
        >
          <div className={cn.mainRepresentativeApplicationLeft}>
            <div className={cn.collapsedArrow}>
              {collapsed ? <IconUp /> : <IconDown />}
            </div>
            <div className={cn.mainRepresentativeName}>{`${
              companyOfficial.subApplication.firstName || '-'
            } ${companyOfficial.subApplication.lastName || '-'}`}</div>
            <div
              className={`${cn.applicationStatus} ${cn.mainRepresentativeApplicationStatus} ${cofApprovalStatusClassName}`}
            >
              {cofApprovalStatus}
            </div>
          </div>
          {applicationStatus !== NewApplicationStatus.ACCEPTED &&
            applicationStatus !== NewApplicationStatus.DECLINED && (
              <div className={cn.mainRepresentativeApplicationRight}>
                {companyOfficial.subApplication.status !==
                  NewApplicationStatus.ACCEPTED && (
                  <div className={cn.cofApplicationDescription}>
                    {cofApplicationDescription}
                  </div>
                )}

                <div
                  className={cn.cofApproveControls}
                  onMouseEnter={() => setFocused(true)}
                  onMouseLeave={() => setFocused(false)}
                >
                  {activePermissions?.includes(
                    PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                  ) &&
                    companyOfficial.subApplication.status !==
                      NewApplicationStatus.DECLINED && (
                      <div className={cn.cofApproveButtonWrapper}>
                        {cofApprovalButton}
                      </div>
                    )}
                  {activePermissions?.includes(
                    PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                  ) &&
                    (companyOfficial.status !== CofApprovalStatus.DECLINED ||
                      (companyOfficial.subApplication.status ===
                        NewApplicationStatus.DECLINED &&
                        companyOfficial.status !==
                          CofApprovalStatus.DECLINED)) && (
                      <div className={cn.cofApproveButtonWrapper}>
                        <ButtonSecondary
                          className={`${cn.cofApproveButton} ${cn.cofApprovalButtonWarning}`}
                          size={ButtonSize.SMALL}
                          text={
                            updateStatusInProgress || updateInProgress
                              ? 'Loading...'
                              : 'Decline'
                          }
                          tabIndex={-1}
                          onClick={() =>
                            onUpdateStatus(CofApprovalStatus.DECLINED)
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            )}
        </div>
      )}
      {collapsed && (
        <div className={cn.companyOfficialWrapper}>
          <div className={cn.companyOfficialApplication}>
            <div className={cn.companyOfficialApplicationLeft}>
              <div className={cn.companyOfficialApplicationTitle}>
                Customer Application:
              </div>
              <div
                className={`${
                  cn.companyOfficialApplicationStatus
                } ${statusClassName} ${
                  companyOfficial?.subApplication.reviewStatus ===
                  ReviewStatus.HAS_DUPLICATE
                    ? cn.declinedStatus
                    : ''
                }`}
              >
                {cofApplicationStatus}
              </div>
            </div>
            <div className={cn.companyOfficialApplicationRight}>
              <div className={cn.linkButtonWrapper} />
              {auditLogAvailable && value.id && (
                <div className={cn.cofChangeLogLinkWrapper}>
                  <NavLink
                    to={`/administration/change-logs?entityType=APPLICATION_TO_APPLICATION&entityId=${companyOfficial?.id}`}
                    className={cn.cofChangeLogLink}
                    target="_blank"
                  >
                    <IconChangeLoClock />
                    Change Log
                  </NavLink>
                </div>
              )}
              {companyOfficial &&
                (companyOfficial?.subApplication.status !==
                  NewApplicationStatus.ACCEPTED ||
                  (companyOfficial?.subApplication.status ===
                    NewApplicationStatus.ACCEPTED &&
                    companyOfficial?.subApplication?.organizationId)) && (
                  <div className={cn.applicationLinkWrapper}>
                    <NavLink
                      to={cofLink as string}
                      className={cn.applicationLink}
                      target="_blank"
                      tabIndex={-1}
                    >
                      {companyOfficial?.subApplication.status !==
                      NewApplicationStatus.ACCEPTED
                        ? 'Go to application'
                        : 'Go to customer'}
                    </NavLink>
                  </div>
                )}
            </div>
          </div>
          <InfoRow title="Position">
            <InputText
              name={`position-${index}`}
              placeholder="Enter position..."
              value={companyOfficialPosition}
              onChange={onChange}
              size={InputSize.SMALL}
              className="borderless"
              tabIndex={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
                  ? -1
                  : 0
              }
              disabled={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
              }
            />
          </InfoRow>
          <InfoRow title="Signature Type">
            <Select
              list={[
                {
                  name: 'None',
                  value: SignatureType.NONE,
                },
                {
                  name: 'Single',
                  value: SignatureType.SINGLE,
                },
              ]}
              name={`signatureType-${index}`}
              value={companyOfficialSignatureType}
              placeholder="Select Signature type"
              onChange={onChange}
              size={SelectSize.SMALL}
              tabIndex={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
                  ? -1
                  : 0
              }
              disabled={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
              }
              borderless
            />
          </InfoRow>
          <InfoRow title="Access Right">
            <Select
              list={[
                {
                  name: 'Viewer',
                  value: AccessRight.VIEWER,
                },
                {
                  name: 'None',
                  value: AccessRight.NONE,
                },
              ]}
              name={`accessRight-${index}`}
              value={companyOfficialAccessRight}
              placeholder="Select Access Right"
              onChange={onChange}
              size={SelectSize.SMALL}
              tabIndex={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
                  ? -1
                  : 0
              }
              disabled={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
              }
              borderless
            />
          </InfoRow>
          <div className={cn.havingControlChekboxWrapper}>
            <Checkbox
              type="checkbox"
              name={`havingControl-${index}`}
              checked={havingControl}
              title="Having a control in company"
              onChange={(e) => setHavingControl(e.target.checked)}
              tabIndex={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
                  ? -1
                  : 0
              }
              disabled={
                !applicationId ||
                disabled ||
                value.status === NewApplicationStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
              }
            />
          </div>
          {havingControl && (
            <>
              {error && <div className={cn.companyOfficialError}>{error}</div>}
              <InfoRow title="Percentage of control">
                <InputText
                  name={`percentage-${index}`}
                  placeholder="Enter percentage of control..."
                  value={companyOfficialPercentage}
                  onChange={onChange}
                  size={InputSize.SMALL}
                  className="borderless"
                  tabIndex={
                    !applicationId ||
                    disabled ||
                    value.status === NewApplicationStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                    !activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !applicationId ||
                    disabled ||
                    value.status === NewApplicationStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                    !activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                    )
                  }
                />
              </InfoRow>
              <InfoRow title="Nature of control">
                <Select
                  list={[
                    {
                      name: 'Direct Control',
                      value: NatureOfControlEnum.DIRECT_CONTROL,
                    },
                    {
                      name: 'Indirect Control',
                      value: NatureOfControlEnum.INDIRECT_CONTROL,
                    },
                  ]}
                  name={`natureOfControl-${index}`}
                  value={companyOfficialNatureOfControl}
                  placeholder="Select Nature of Control"
                  onChange={onChange}
                  size={SelectSize.SMALL}
                  tabIndex={
                    !applicationId ||
                    disabled ||
                    value.status === NewApplicationStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                    !activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !applicationId ||
                    disabled ||
                    value.status === NewApplicationStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.DECLINED ||
                    companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                    !activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                    )
                  }
                  borderless
                />
              </InfoRow>
            </>
          )}
          <div className={cn.companyOfficialControls}>
            <button
              type="button"
              className={cn.companyOfficialUpdateButton}
              onClick={onUpdate}
              disabled={
                disabled ||
                !value.position ||
                companyOfficial?.status === CofApprovalStatus.DECLINED ||
                companyOfficial?.status === CofApprovalStatus.ACCEPTED ||
                !activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )
              }
              tabIndex={-1}
            >
              {updateInProgress ? 'Loading...' : 'Save settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOfficial;
